import React, { useState, useEffect, useMemo, useRef } from "react";
import * as Chart from "chart.js";
import * as XLSX from "xlsx";
import {
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { fetchDailyOccupancy } from "../../Store/Apis/ReservationApi";
import dayjs from "dayjs";

const { MonthPicker, YearPicker } = DatePicker;

// Register Chart.js components
Chart.Chart.register(
  Chart.CategoryScale,
  Chart.LinearScale,
  Chart.PointElement,
  Chart.LineElement,
  Chart.BarElement,
  Chart.Title,
  Chart.Tooltip,
  Chart.Legend
);

const RoomOccupancyChart = ({ roomsData: propRoomsData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // State management - Default to yearly view
  const [filterType, setFilterType] = useState("Year");
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs());
  // Week view anchor (start of week) & Quarter selection (exactly 3 months)
  const weekStart = (d) => d.startOf("day").subtract(d.day(), "day"); // Sunday-based week start
  const [selectedWeek, setSelectedWeek] = useState(weekStart(dayjs()));
  const [selectedQuarter, setSelectedQuarter] = useState(null); // 'Q1' | 'Q2' | 'Q3' | 'Q4'
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [apiRoomsData, setApiRoomsData] = useState(null);
  const [error, setError] = useState(null);

  // Filter function to get only rooms (contains 'Room' in roomTypeName)
  const filterRoomsOnly = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(item => 
      item.roomTypeName && 
      item.roomTypeName.toLowerCase().includes('room')
    );
  };

  // Fetch data from API
  useEffect(() => {
    const fetchOccupancyData = async () => {
      if (propRoomsData) {
        // If data is passed as prop, filter and use it
        const filteredRooms = filterRoomsOnly(propRoomsData);
        setApiRoomsData(filteredRooms);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Convert selected parameters to API format based on filter type
        let apiDate;
        if (filterType === "Day") {
          apiDate = selectedDate.format("YYYY-MM-DD");
        } else if (filterType === "Week") {
          apiDate = selectedWeek.format("YYYY-MM-DD"); // week anchor (start)
        } else if (filterType === "Month") {
          apiDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
        } else if (filterType === "Quarter") {
          const startMonth =
            selectedQuarter === "Q1"
              ? 0
              : selectedQuarter === "Q2"
              ? 3
              : selectedQuarter === "Q3"
              ? 6
              : 9;
          apiDate = (
            selectedQuarter
              ? selectedYear.month(startMonth)
              : selectedYear.startOf("year")
          )
            .startOf("month")
            .format("YYYY-MM-DD");
        } else {
          apiDate = selectedYear.startOf("year").format("YYYY-MM-DD");
        }

        console.log("Fetching room occupancy data for date:", apiDate);

        const response = await fetchDailyOccupancy(apiDate);
        console.log("Raw API Response:", response);

        if (response && response.data) {
          console.log("API Data received:", response.data);
          // Filter to only include rooms
          const filteredRooms = filterRoomsOnly(response.data);
          console.log("Filtered rooms data:", filteredRooms);
          setApiRoomsData(filteredRooms);
        } else {
          console.warn("No data received from API");
          setApiRoomsData([]);
        }
      } catch (error) {
        console.error("Error fetching room occupancy data:", error);
        setError(error.message);
        setApiRoomsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOccupancyData();
  }, [
    filterType,
    selectedYear,
    selectedMonth,
    selectedDate,
    selectedWeek,
    selectedQuarter,
    propRoomsData,
  ]);

  // Normalize API data to match component expectations
  const normalizeApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.warn("Invalid API data:", apiData);
      return [];
    }

    return apiData.map((room) => ({
      roomTypeID: room.roomTypeID,
      roomTypeName: room.roomTypeName,
      status: room.status,
      statusDescription: room.statusDescription,
      bookings: room.bookings
        ? room.bookings.map((booking) => ({
            // Map API booking fields to expected format
            order_id: booking.order_id || booking.orderId || Math.random(), // Generate if missing
            service_name: booking.service_name || booking.roomName || "ACCOMMODATION",
            checkInDate: booking.checkInDate,
            checkOutDate: booking.checkOutDate,
            customer_name: booking.customer_name || booking.customerName,
            order_date: booking.order_date || booking.orderDate,
            check_out_date: booking.check_out_date || booking.checkOutOrderDate || booking.checkOutDate,
            order_status: booking.order_status || booking.orderStatus,
            room_type_name: booking.room_type_name || booking.roomTypeName,
          }))
        : [],
    }));
  };

  // Use API data or fallback to sample data
  const currentRoomsData = useMemo(() => {
    if (apiRoomsData) {
      const normalized = normalizeApiData(apiRoomsData);
      console.log("Normalized room data for chart:", normalized);
      return normalized;
    }

    // Sample room data as fallback
    return [
      {
        roomTypeID: 474,
        roomTypeName: "Room 1",
        status: 0,
        statusDescription: "Unoccupied",
        bookings: [],
      },
      {
        roomTypeID: 475,
        roomTypeName: "Room 2",
        status: 1,
        statusDescription: "Occupied",
        bookings: [
          {
            order_id: 8,
            service_name: "ACCOMMODATION",
            checkInDate: "2025-09-10T10:34:00",
            checkOutDate: "2025-09-24T22:00:00",
            customer_name: "TestDIS",
            order_date: "2025-09-10T10:34:00",
            check_out_date: "2025-09-24T22:00:00",
            order_status: "Confirmed",
            room_type_name: "DINNER BED & BREAKFAST",
          },
        ],
      },
    ];
  }, [apiRoomsData]);

  // Helper function: Convert UTC timestamp to Africa/Johannesburg timezone
  const convertToLocalTimezone = (utcTimestamp) => {
    if (!utcTimestamp) return new Date();
    const date = new Date(utcTimestamp);
    // Convert to Johannesburg timezone (UTC+2)
    return new Date(
      date.toLocaleString("en-US", { timeZone: "Africa/Johannesburg" })
    );
  };

  // Helper function: Format date for display
  const formatDateForDisplay = (
    filterType,
    selectedYear,
    selectedMonth,
    selectedDate
  ) => {
    if (filterType === "Day") return selectedDate.format("dddd, MMMM D, YYYY");
    if (filterType === "Week") {
      const start = selectedWeek;
      const end = selectedWeek.add(6, "day");
      return `Week: ${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    }
    if (filterType === "Month") return selectedMonth.format("MMMM YYYY");
    if (filterType === "Quarter")
      return selectedQuarter
        ? `${selectedQuarter} ${selectedYear.format("YYYY")}`
        : "Quarter (select)";
    return selectedYear.format("YYYY");
  };

  // Helper function: Create time buckets based on filter type and selected date
  const createTimeBuckets = (
    filterType,
    selectedYear,
    selectedMonth,
    selectedDate
  ) => {
    const buckets = [];

    if (filterType === "Day") {
      // Hourly buckets for the selected day
      const date = selectedDate.toDate();
      for (let hour = 0; hour < 24; hour++) {
        const bucketStart = new Date(date);
        bucketStart.setHours(hour, 0, 0, 0);
        const bucketEnd = new Date(bucketStart);
        bucketEnd.setHours(hour + 1, 0, 0, 0);

        buckets.push({
          index: hour,
          start: bucketStart,
          end: bucketEnd,
          label: `${hour.toString().padStart(2, "0")}:00`,
          totalSeconds: 3600, // 1 hour
        });
      }
    } else if (filterType === "Week") {
      const start = selectedWeek;
      for (let i = 0; i < 7; i++) {
        const day = start.add(i, "day");
        const bucketStart = day.startOf("day").toDate();
        const bucketEnd = day.add(1, "day").startOf("day").toDate();
        buckets.push({
          index: i,
          start: bucketStart,
          end: bucketEnd,
          label: day.format("ddd D"),
          totalSeconds: 86400,
        });
      }
    } else if (filterType === "Month") {
      // Daily buckets for the selected month
      const year = selectedMonth.year();
      const month = selectedMonth.month(); // 0-indexed
      const daysInMonth = selectedMonth.daysInMonth();

      for (let day = 1; day <= daysInMonth; day++) {
        const bucketStart = new Date(year, month, day, 0, 0, 0, 0);
        const bucketEnd = new Date(year, month, day + 1, 0, 0, 0, 0);

        buckets.push({
          index: day - 1,
          start: bucketStart,
          end: bucketEnd,
          label: `${day}`,
          totalSeconds: 86400, // 24 hours
        });
      }
    } else if (filterType === "Quarter") {
      const map = {
        Q1: [0, 1, 2],
        Q2: [3, 4, 5],
        Q3: [6, 7, 8],
        Q4: [9, 10, 11],
      };
      const months = map[selectedQuarter] || [];
      months.forEach((m, idx) => {
        const ms = selectedYear.month(m).startOf("month");
        const bucketStart = ms.toDate();
        const bucketEnd = ms.add(1, "month").startOf("month").toDate();
        const daysInMonth = ms.daysInMonth();
        buckets.push({
          index: idx,
          start: bucketStart,
          end: bucketEnd,
          label: ms.format("MMM"),
          totalSeconds: daysInMonth * 86400,
        });
      });
    } else if (filterType === "Year") {
      // Monthly buckets for the selected year
      const year = selectedYear.year();
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      for (let month = 0; month < 12; month++) {
        const bucketStart = new Date(year, month, 1, 0, 0, 0, 0);
        const bucketEnd = new Date(year, month + 1, 1, 0, 0, 0, 0);
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        buckets.push({
          index: month,
          start: bucketStart,
          end: bucketEnd,
          label: monthNames[month],
          totalSeconds: daysInMonth * 86400, // days * 24 hours
        });
      }
    }

    return buckets;
  };

  // Helper function: Split booking duration across time buckets (handles overlapping bookings correctly)
  const splitBookingAcrossBuckets = (checkIn, checkOut, buckets) => {
    const splits = [];
    const checkInTime = convertToLocalTimezone(checkIn).getTime();
    const checkOutTime = convertToLocalTimezone(checkOut).getTime();

    buckets.forEach((bucket) => {
      const bucketStart = bucket.start.getTime();
      const bucketEnd = bucket.end.getTime();

      // Calculate overlap between booking and bucket
      const overlapStart = Math.max(checkInTime, bucketStart);
      const overlapEnd = Math.min(checkOutTime, bucketEnd);

      if (overlapStart < overlapEnd) {
        const occupiedSeconds = (overlapEnd - overlapStart) / 1000;
        splits.push({
          bucketIndex: bucket.index,
          occupiedSeconds: occupiedSeconds,
        });
      }
    });

    return splits;
  };

  // Core function: Calculate occupancy data for chart
  const calculateOccupancyData = useMemo(() => {
    if (!currentRoomsData || currentRoomsData.length === 0) {
      return { chartData: null, summaryStats: null };
    }

    try {
      const buckets = createTimeBuckets(
        filterType,
        selectedYear,
        selectedMonth,
        selectedDate
      );
      const totalRooms = currentRoomsData.length;

      console.log("Calculating room occupancy with:", {
        totalRooms,
        filterType,
        selectedYear: selectedYear.format("YYYY"),
        selectedMonth: selectedMonth.format("YYYY-MM"),
        selectedDate: selectedDate.format("YYYY-MM-DD"),
        bucketsCount: buckets.length,
      });

      // Calculate total time period in seconds across all buckets
      const totalPeriodSeconds = buckets.reduce(
        (total, bucket) => total + bucket.totalSeconds,
        0
      );

      // Initialize occupancy tracking per room
      const roomOccupancyData = currentRoomsData.map((room) => ({
        roomId: room.roomTypeID,
        roomName: room.roomTypeName,
        totalOccupiedSeconds: 0,
        bookingsCount: room.bookings ? room.bookings.length : 0,
      }));

      // Process each room's bookings
      currentRoomsData.forEach((room, roomIndex) => {
        if (room.bookings && room.bookings.length > 0) {
          room.bookings.forEach((booking) => {
            if (booking.checkInDate && booking.checkOutDate) {
              console.log(`Processing booking for room ${room.roomTypeName}:`, {
                checkIn: booking.checkInDate,
                checkOut: booking.checkOutDate,
                customer: booking.customer_name,
              });

              const splits = splitBookingAcrossBuckets(
                booking.checkInDate,
                booking.checkOutDate,
                buckets
              );

              // Sum up all occupied seconds for this room across all time buckets
              const totalOccupiedForThisBooking = splits.reduce(
                (total, split) => total + split.occupiedSeconds,
                0
              );
              roomOccupancyData[roomIndex].totalOccupiedSeconds +=
                totalOccupiedForThisBooking;
            } else {
              console.warn(
                `Missing check-in/out dates for booking in room ${room.roomTypeName}:`,
                booking
              );
            }
          });
        }
      });

      // Calculate occupancy percentages for each room
      const labels = roomOccupancyData.map((room) => room.roomName);
      const occupancyPercentages = roomOccupancyData.map((room) => {
        const occupancyPercent =
          totalPeriodSeconds > 0
            ? (room.totalOccupiedSeconds / totalPeriodSeconds) * 100
            : 0;
        return Math.round(occupancyPercent * 100) / 100; // Round to 2 decimal places
      });

      // Calculate summary statistics
      const avgOccupancy =
        occupancyPercentages.length > 0
          ? Math.round(
              (occupancyPercentages.reduce((a, b) => a + b, 0) /
                occupancyPercentages.length) *
                100
            ) / 100
          : 0;

      const maxOccupancy =
        occupancyPercentages.length > 0 ? Math.max(...occupancyPercentages) : 0;

      const summaryStats = {
        avgOccupancy,
        maxOccupancy,
        totalRooms,
        timeRange: `${filterType} view for ${formatDateForDisplay(
          filterType,
          selectedYear,
          selectedMonth,
          selectedDate
        )}`,
      };

      const chartData = {
        labels,
        datasets: [
          {
            label: "Room Occupancy %",
            data: occupancyPercentages,
            borderColor: "#faae42",
            backgroundColor: "rgba(250, 174, 66, 0.2)",
            borderWidth: 2,
          },
        ],
      };

      console.log("Room occupancy calculation results:", {
        chartData,
        summaryStats,
        occupancyPercentages,
        roomOccupancyData,
      });

      return { chartData, summaryStats, roomOccupancyData };
    } catch (error) {
      console.error("Error calculating room occupancy data:", error);
      return { chartData: null, summaryStats: null };
    }
  }, [
    currentRoomsData,
    filterType,
    selectedYear,
    selectedMonth,
    selectedDate,
    selectedWeek,
    selectedQuarter,
  ]);

  // Update state when calculation completes
  useEffect(() => {
    const result = calculateOccupancyData;
    setChartData(result.chartData);
    setSummaryStats(result.summaryStats);
  }, [calculateOccupancyData]);

  // Chart configuration
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `Room Occupancy Performance - ${filterType} Period`,
        font: {
          size: 16,
          weight: "bold",
        },
      },
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: function (tooltipItems) {
            const dataIndex = tooltipItems[0].dataIndex;
            const roomData =
              calculateOccupancyData.roomOccupancyData?.[dataIndex];
            return roomData ? roomData.roomName : "Room";
          },
          label: function (context) {
            const percentage = context.parsed.y;
            const roomData =
              calculateOccupancyData.roomOccupancyData?.[context.dataIndex];
            const bookingsCount = roomData?.bookingsCount || 0;
            const totalHours =
              Math.round(roomData?.totalOccupiedSeconds / 3600) || 0;
            return [
              `Occupancy: ${percentage}%`,
              `Active bookings: ${bookingsCount}`,
              `Total occupied time: ${totalHours} hours`,
              `Period: ${
                summaryStats?.timeRange?.replace(
                  `${filterType} view for `,
                  ""
                ) || "N/A"
              }`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 25,
          callback: function (value) {
            return value + "%";
          },
        },
        title: {
          display: true,
          text: "Occupancy Percentage (%)",
          font: {
            weight: "bold",
          },
        },
      },
      x: {
        title: {
          display: true,
          text: "Room Names",
          font: {
            weight: "bold",
          },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    animation: {
      duration: 150, // Quick animations on filter change
    },
  };

  // Update chart when data changes
  useEffect(() => {
    if (chartRef.current && chartData) {
      // Destroy existing chart
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      // Create new chart
      const ctx = chartRef.current.getContext("2d");
      chartInstance.current = new Chart.Chart(ctx, {
        type: "bar",
        data: chartData,
        options: chartOptions,
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, chartOptions]);

  // Export functions
  const exportToExcel = () => {
    if (
      !chartData ||
      !chartData.labels ||
      !calculateOccupancyData.roomOccupancyData
    ) {
      alert("No data available for export");
      return;
    }

    // Prepare data for Excel with detailed room information
    const excelData = chartData.labels.map((roomName, index) => {
      const roomData = calculateOccupancyData.roomOccupancyData[index];
      const occupancyPercentage = chartData.datasets[0].data[index];

      return {
        "Room Name": roomName,
        "Occupancy Percentage": `${occupancyPercentage}%`,
        "Total Bookings": roomData?.bookingsCount || 0,
        "Total Occupied Hours": Math.round(
          (roomData?.totalOccupiedSeconds || 0) / 3600
        ),
        "Time Period":
          summaryStats?.timeRange?.replace(`${filterType} view for `, "") ||
          "N/A",
        "Filter Type": filterType,
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 20 }, // Room Name
      { wch: 18 }, // Occupancy Percentage
      { wch: 15 }, // Total Bookings
      { wch: 20 }, // Total Occupied Hours
      { wch: 25 }, // Time Period
      { wch: 12 }, // Filter Type
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Room Occupancy Report");

    // Generate filename
    const dateString = selectedYear.format("YYYY");
    const filename = `Room_Occupancy_Report_${filterType}_${dateString}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  const exportToPNG = async () => {
    if (!chartInstance.current) {
      alert("Chart not available for export");
      return;
    }

    try {
      // Use Chart.js built-in toBase64Image method
      const url = chartInstance.current.toBase64Image();
      const link = document.createElement("a");
      link.download = `room-occupancy-${filterType.toLowerCase()}-${selectedYear.format(
        "YYYY"
      )}.png`;
      link.href = url;
      link.click();
    } catch (error) {
      console.error("Error exporting chart:", error);
      alert("Error exporting chart. Please try again.");
    }
  };

  return (
    <div>
      {/* Filter Controls Card */}
      <Card title="Room Filter Controls" style={{ marginBottom: "24px" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                View Type
              </label>
              <Select
                value={filterType}
                onChange={setFilterType}
                style={{ width: "100%" }}
                size="large"
                options={[
                  { label: "Daily (Hourly)", value: "Day" },
                  { label: "Weekly (Daily)", value: "Week" },
                  { label: "Monthly (Daily)", value: "Month" },
                  { label: "Quarterly (Monthly)", value: "Quarter" },
                  { label: "Yearly (Monthly)", value: "Year" },
                ]}
              />
            </div>
          </Col>

          {/* Conditional Date Pickers */}
          {filterType === "Year" && (
            <Col xs={24} sm={8} md={6}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: 500,
                  }}
                >
                  Select Year
                </label>
                <DatePicker
                  picker="year"
                  value={selectedYear}
                  onChange={(y) => {
                    setSelectedYear(y);
                  }}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="Choose a year"
                />
              </div>
            </Col>
          )}

          {filterType === "Month" && (
            <Col xs={24} sm={8} md={6}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: 500,
                  }}
                >
                  Select Month & Year
                </label>
                <DatePicker
                  picker="month"
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="Choose a month"
                  format="MMMM YYYY"
                />
              </div>
            </Col>
          )}

          {filterType === "Day" && (
            <Col xs={24} sm={8} md={6}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: 500,
                  }}
                >
                  Select Date
                </label>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  style={{ width: "100%" }}
                  size="large"
                  format="YYYY-MM-DD"
                  placeholder="Choose a date"
                />
              </div>
            </Col>
          )}
          {filterType === "Week" && (
            <Col xs={24} sm={8} md={6}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: 500,
                  }}
                >
                  Select Week (pick any date inside)
                </label>
                <DatePicker
                  value={selectedWeek}
                  onChange={(d) => d && setSelectedWeek(weekStart(d))}
                  style={{ width: "100%" }}
                  size="large"
                  format="YYYY-MM-DD"
                  placeholder="Choose week"
                />
              </div>
            </Col>
          )}
          {filterType === "Quarter" && (
            <>
              <Col xs={24} sm={8} md={6}>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: 500,
                    }}
                  >
                    Select Year
                  </label>
                  <DatePicker
                    picker="year"
                    value={selectedYear}
                    onChange={(y) => {
                      setSelectedYear(y);
                      setSelectedQuarter(null);
                    }}
                    style={{ width: "100%" }}
                    size="large"
                    placeholder="Choose a year"
                  />
                </div>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: 500,
                    }}
                  >
                    Select Quarter
                  </label>
                  <Select
                    value={selectedQuarter}
                    onChange={setSelectedQuarter}
                    style={{ width: "100%" }}
                    size="large"
                    placeholder="Choose quarter"
                    options={[
                      { label: "Q1 (Jan-Mar)", value: "Q1" },
                      { label: "Q2 (Apr-Jun)", value: "Q2" },
                      { label: "Q3 (Jul-Sep)", value: "Q3" },
                      { label: "Q4 (Oct-Dec)", value: "Q4" },
                    ]}
                  />
                </div>
              </Col>
            </>
          )}

          {/* Export Buttons */}
          <Col xs={24} sm={16} md={12} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={exportToExcel}
              disabled={!chartData || loading}
              style={{ marginRight: "8px" }}
              size="large"
            >
              Export Excel
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToPNG}
              disabled={!chartInstance.current || loading}
              size="large"
            >
              Export PNG
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error Loading Room Data"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: "24px" }}
        />
      )}

      {/* Summary Statistics */}
      {summaryStats && !loading && (
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Average Room Occupancy"
                value={summaryStats.avgOccupancy}
                precision={1}
                suffix="%"
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Peak Room Occupancy"
                value={summaryStats.maxOccupancy}
                precision={1}
                suffix="%"
                valueStyle={{ color: "rgb(204, 148, 20)" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Total Rooms"
                value={summaryStats.totalRooms}
                valueStyle={{ color: "rgb(4, 63, 110)" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Card>
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "4px",
                  }}
                >
                  Current View
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 500,
                    color: "#262626",
                  }}
                >
                  {summaryStats.timeRange.replace(
                    `${filterType} view for `,
                    ""
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Chart Container */}
      <Card
        title={`Room Performance - ${filterType} Period`}
        style={{ minHeight: "600px" }}
      >
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
            }}
          >
            <Spin size="large" />
            <span style={{ marginLeft: "16px", fontSize: "16px" }}>
              Loading room occupancy data...
            </span>
          </div>
        )}

        {!loading && !chartData && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              color: "#666",
            }}
          >
            <CalendarOutlined
              style={{
                fontSize: "64px",
                marginBottom: "16px",
                color: "#d9d9d9",
              }}
            />
            <h3 style={{ color: "#666" }}>No Room Data Available</h3>
            <p style={{ textAlign: "center", maxWidth: "300px" }}>
              No room booking data found for the selected {filterType.toLowerCase()}.
              Try selecting a different time period.
            </p>
            <Button
              type="primary"
              onClick={() => {
                setSelectedYear(dayjs());
                setSelectedMonth(dayjs());
                setSelectedDate(dayjs());
              }}
            >
              Reset to Current {filterType}
            </Button>
          </div>
        )}

        {!loading && chartData && (
          <div style={{ height: "500px", width: "100%" }}>
            <canvas
              ref={chartRef}
              style={{ maxHeight: "500px", width: "100%" }}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default RoomOccupancyChart;